import loadable from '@loadable/component';
import React, { FunctionComponent, useState } from 'react';
import { ACCOUNT_LINK, ACCOUNT_PRO_LINK, CART_LINK, HOMEPAGE_LINK, SHOWROOM_LINK, SUPPORT_FAQ_LINK } from '../../../constants/links';
import { useCartHeader } from '../../../hooks/apollo/cart-header/cart-header.hooks';
import { useCustomer } from '../../../hooks/apollo/customer/customer.hooks';
import { Link } from '../../common-components/link/link.component';
import { ProjectHeaderDropdown } from '../../project-components/project-header-dropdown/project-header-dropdown.component';
import { BuildWFergusonSvg, ProBadgeSvg } from '../../svg/general.component';
import { AccountBoxIcon, CartIcon, NotificationBellIcon } from '../../svg/icons.component';
import { AccountDropdown } from '../account-dropdown/account-dropdown.component';
import { CallCenterLink } from '../call-center-link/call-center-link.component';
import { ChatButton } from '../chat-button/chat-button.component';
import { DesktopNav } from '../desktop-nav/desktop-nav.component';
import { MobileNav } from '../mobile-nav/mobile-nav.component';
import { SearchBar } from '../search-bar/search-bar.component';
import { UniversalLocationIndicator } from '../universal-location-indicator/universal-location-indicator.component';
import { Badge } from '../../common-components/badge/badge.component';
import { ClickableElement } from '../../buttons';
import { useFeature } from '../../../hooks/features/features.hooks';
import { FEATURE_FLAGS, OVERLAY_MARKER } from '../../../constants/general';
import { useTrackEvent } from '../../../hooks/analytics/analytics.hooks';
import { buildGTMHeaderNotificationsClick } from '../../../helpers/analytics/gtm/gtm-utils.helper';
import { cartIconStyles, navigationStyles, proBadge } from './header.css';
import { GlobalContentConstruct } from './global-content-construct.component';

const LoadableNotificationFeed = loadable(
	() => import(/* webpackChunkName: "notification-feed" */ '../notification-feed/notification-feed.component'),
	{
		resolveComponent: ({ NotificationFeed }) => NotificationFeed
	}
);

const CartQuantityIndicator: FunctionComponent<{ quantity: number }> = ({ quantity }) => {
	const isOver99 = quantity > 99;
	const modifiedFontSize = isOver99 ? `f7` : null;

	return (
		<div className="h2 tc">
			<div className={`relative top-0 ${modifiedFontSize}`} style={{ left: '.15em' }} data-automation="header-cart-quantity-amount">
				{isOver99 ? '99+' : quantity}
			</div>
			<CartIcon className={`f2 ${cartIconStyles}`} />
		</div>
	);
};

const NotificationsBadge: FunctionComponent = () => {
	const { data: customerData } = useCustomer();
	const trackEvent = useTrackEvent();
	const unreadNotificationCount = customerData?.customer?.unreadNotificationCount ?? 0;
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const enableProjectNotifications = useFeature<boolean>(FEATURE_FLAGS.PROJECT_NOTIFICATIONS);

	const handleClick = () => {
		setIsOpen(!isOpen);
		trackEvent(buildGTMHeaderNotificationsClick());
	};

	return enableProjectNotifications ? (
		<>
			<ClickableElement ariaLabel="notifications" onClick={handleClick}>
				<Badge count={unreadNotificationCount}>
					<NotificationBellIcon className="f2 nr2 theme-grey-medium" />
				</Badge>
			</ClickableElement>
			{isOpen && <LoadableNotificationFeed setIsOpen={setIsOpen} />}
		</>
	) : null;
};

const CartLink: FunctionComponent<{ isMobile?: boolean }> = ({ isMobile = false }) => {
	const { quantity } = useCartHeader();
	const linkStyle = isMobile ? 'dn-ns bg-theme-primary pa2 order-2 h-100' : '';
	return (
		<Link
			url={CART_LINK}
			className={linkStyle}
			color={isMobile ? 'white' : 'secondary'}
			automationHook={`header-cart${isMobile ? '-mobile' : ''}`}
			ariaLabel={`View ${quantity} items currently in cart`}>
			<div className="flex items-center h-100">
				<CartQuantityIndicator quantity={quantity} />
			</div>
		</Link>
	);
};

const ProSignupLink: FunctionComponent = () => {
	const { hasAccount } = useCustomer();

	return hasAccount ? null : (
		<Link url={ACCOUNT_PRO_LINK} className="dn flex-l flex-row mh2 pt1 hover-theme-primary" color="secondary">
			<div className="f7 b mb1" data-testid="ab-projects-in-header-pro-banner">
				Are you a Pro?
			</div>
			<div className="f7 ml1">
				<span className="di v-mid">Get</span>
				<ProBadgeSvg className={`${proBadge} ph1 theme-accent di v-mid`} />
				<span className="di v-mid">Pricing</span>
			</div>
		</Link>
	);
};

const MobileAccountLink: FunctionComponent = () => {
	const { data: customerData } = useCustomer();
	const unreadNotificationCount = customerData?.customer?.unreadNotificationCount ?? 0;
	const enableProjectNotifications = useFeature<boolean>(FEATURE_FLAGS.PROJECT_NOTIFICATIONS);

	return (
		<Link url={ACCOUNT_LINK} className="dib dn-ns hover-theme-primary h-auto-ns w-auto-ns ph2" color="secondary">
			<div className="tc">
				{enableProjectNotifications ? (
					<Badge count={unreadNotificationCount} hideCount={true} noMargin={true}>
						<AccountBoxIcon className="f3" />
					</Badge>
				) : (
					<AccountBoxIcon className="f3" />
				)}
				<div>Account</div>
			</div>
		</Link>
	);
};

export const Header: FunctionComponent = () => {
	return (
		<header className="bg-theme-white dn-p" data-testid="headerComponent">
			<div className="bt-ns bw2-ns b--theme-primary f6 sans-serif">
				<div className="flex-ns flex-row-ns justify-end-ns center mw9">
					<ProSignupLink />
					<div className={`${navigationStyles} bg-theme-grey-light-ns br-pill br--bottom fn fr-ns pv0 pv1-ns`}>
						<nav className="flex flex-row justify-end items-center h-100 h-auto-ns fn fr-ns mh4-ns" aria-label="Helpful links">
							<Link
								url={HOMEPAGE_LINK}
								className={`dib dn-ns h-100 w-20 ml2 mr-auto`}
								color="secondary"
								ariaLabel="Navigate to home page">
								<BuildWFergusonSvg className="h-100 w-100" />
							</Link>
							<MobileAccountLink />
							<div className="db dn-ns">
								<ProjectHeaderDropdown />
							</div>
							<CallCenterLink iconClass="f3 f5-ns pb1 pb0-ns" />
							<div className="w-auto-ns tc ph2 ph0-ns mh1 mh0-ns dn db-ns">
								<ChatButton automationHook="header-chat" />
							</div>
							<Link url={SHOWROOM_LINK} className={`dn dib-ns hover-theme-primary h-100 h-auto-ns mr3-ns`} color="secondary">
								Showrooms
							</Link>
							<Link url={SUPPORT_FAQ_LINK} className={`dn dib-ns hover-theme-primary h-100 h-auto-ns`} color="secondary">
								Help
							</Link>
							<CartLink isMobile={true} />
						</nav>
					</div>
				</div>
				{/* Desktop Only */}
				<div className={`mw9 dn db-ns mb2 center`}>
					<div className={`flex flex-row justify-between items-end w-100`}>
						<Link url={HOMEPAGE_LINK} className={`w-10 dib h3`} color="secondary" ariaLabel="Navigate to home page">
							<BuildWFergusonSvg className="ml2-m h-100 w-100" />
						</Link>

						<div className="flex flex-column flex-nowrap pt2 pl4 w-90 relative">
							{/* Settings z-index to z-2 cause it needs to be above the search bar which is z-1*/}
							<div className="absolute top--1 z-2">
								<UniversalLocationIndicator />
							</div>
							<SearchBar />
							<div className="fr flex justify-between items-center self-end pl4 pr3 w-60">
								<ProjectHeaderDropdown />
								<AccountDropdown />
								<NotificationsBadge />
								<div className="f5 b flex flex-column flex-row-ns items-center justify-center">
									<CartLink />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className={`${OVERLAY_MARKER} dn db-ns`} />
				<DesktopNav />
				<GlobalContentConstruct />
				{/* Mobile Only */}
				<MobileNav />
				<div className={`${OVERLAY_MARKER} dn-ns`} />
				<div className="tc theme-black pa2 dn-ns">
					<UniversalLocationIndicator />
				</div>
			</div>
		</header>
	);
};
